import { RootState } from "@/services/state/store";
import { Box, Text } from "@chakra-ui/react";
import { Node } from "@xyflow/react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Table } from "../Table/Table";
import { Editor } from "@monaco-editor/react";
import { DropDown } from "../Select/DropDown";

export const GatewayProperties = ({
  selectedNode,
  gateWayDetails,
  setGatewayDetails,
}: {
  selectedNode: Node | undefined;
  gateWayDetails: { expression: string; result: string; resultName: string }[];
  setGatewayDetails: React.Dispatch<
    React.SetStateAction<
      {
        expression: string;
        result: string;
        resultName: string;
      }[]
    >
  >;
}) => {
  const edges = useSelector((state: RootState) => state.processModeler.edges);
  const nodes = useSelector((state: RootState) => state.processModeler.nodes);

  const [nodeEdges, setNodeEdges] = useState<Node[]>([]);

  useEffect(() => {
    if (selectedNode) {
      const ne = edges
        .filter((edge) => edge.source === selectedNode?.id)
        .map((edge) => nodes.find((node) => node.id === edge.target))
        .filter((node) => node !== undefined) as Node[];

      setNodeEdges(ne);
    }
  }, [selectedNode, edges, nodes]);

  const editorOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: false },
    readOnly: false,
  };

  const handleOptionChange = (selectedOptionId: string, index: number) => {
    setGatewayDetails((prevDetails) => {
      // Find the index where the selected option is currently assigned
      const existingIndex = prevDetails.findIndex(
        (detail) => detail.result === selectedOptionId
      );

      // Create a copy of the details to update
      const updatedDetails = [...prevDetails];

      // Replace the existing assignment if found
      if (existingIndex !== -1 && existingIndex !== index) {
        updatedDetails[existingIndex].result = updatedDetails[index].result;
        updatedDetails[existingIndex].resultName =
          updatedDetails[index].resultName;
      }

      // Update the current row with the new selection
      const selectedNode = nodeEdges.find(
        (node) => node.id === selectedOptionId
      );
      updatedDetails[index].result = selectedOptionId;
      updatedDetails[index].resultName =
        (selectedNode?.data.label as string) || "";

      return updatedDetails;
    });
  };

  const columns = [
    {
      Header: "Condition",
      accessor: "condition",
    },
    {
      Header: "Expression",
      accessor: "expression",
    },
    {
      Header: "Result",
      accessor: "result",
    },
  ];

  const data = gateWayDetails.map((edge, index) => {
    const availableOptions = nodeEdges.map((ne) => ({
      id: ne.id,
      name: ne.data.label as string,
      component: <Text>{ne.data.label as string}</Text>,
    }));

    return {
      condition:
        selectedNode?.data.nodeTypCd === "OR_GW" ? (
          <Text>{index !== gateWayDetails.length - 1 ? "If" : "Else"}</Text>
        ) : selectedNode?.data.nodeTypCd === "XOR_GW" ? (
          <Text>
            {index === 0
              ? "If"
              : index !== gateWayDetails.length - 1
              ? "Else if"
              : "Else"}
          </Text>
        ) : (
          <Text>Default</Text>
        ),
      expression:
        selectedNode?.data.nodeTypCd === "AND_GW" ? null : index !==
          gateWayDetails.length - 1 ? (
          <Box borderWidth="1px" borderRadius="md" overflow="hidden">
            <Editor
              value={edge.expression}
              defaultLanguage="groovy"
              theme="vs-dark"
              height="10vh"
              options={editorOptions}
              onChange={(e) => {
                const updatedDetails = [...gateWayDetails];
                if (e) {
                  updatedDetails[index].expression = e;
                  setGatewayDetails(updatedDetails);
                }
              }}
            />
          </Box>
        ) : null,
      result: (
        <DropDown
          selected={{
            id: edge.result,
            name: edge.resultName,
            component: <Text>{edge.resultName}</Text>,
          }}
          title={<Text></Text>}
          options={availableOptions}
          onOptionChange={(option) => {
            if (!Array.isArray(option)) {
              handleOptionChange(option.id, index);
            }
          }}
        />
      ),
    };
  });

  return (
    <Box>
      <Table data={data} columns={columns} paginationEnabled={false} />
    </Box>
  );
};
